import React, { useState } from 'react';
import { FileText, Trash2, ChevronDown, ChevronRight, Mail, Phone } from 'lucide-react';
import { fmtDateTime, fmtDate } from './constants';

const ConsentPill = ({ ok, label }) => (
  <span className={`inline-block text-[10px] tracking-[0.16em] uppercase font-semibold px-2 py-0.5 border ${
    ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'
  }`}>
    {label}: {ok ? 'YES' : 'NO'}
  </span>
);

const PatientFormsTab = ({ forms, onDelete }) => {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="bg-white border border-black/5" data-testid="patient-forms-tab">
      <div className="px-6 py-5 border-b border-black/5 flex items-center gap-2">
        <FileText size={18} className="text-[#c9a961]" strokeWidth={1.5} />
        <h3
          className="font-serif text-xl text-[#1c1e20]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Signed consent forms ({forms.length})
        </h3>
      </div>

      {forms.length === 0 ? (
        <p className="text-center text-[#6b6b6b] py-14 text-sm">No consent forms signed yet.</p>
      ) : (
        <ul>
          {forms.map((f) => {
            const isOpen = expanded === f.id;
            const address = [f.address, f.city, f.state, f.zip_code].filter(Boolean).join(', ');
            return (
              <li key={f.id} className="border-b border-black/5" data-testid={`consent-row-${f.id}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : f.id)}
                  data-testid={`consent-toggle-${f.id}`}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#faf6f0]/60 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown size={16} className="text-[#c9a961]" /> : <ChevronRight size={16} className="text-[#c9a961]" />}
                    <div>
                      <p className="font-semibold text-[#1c1e20] text-[14px]">{f.full_name}</p>
                      <p className="text-[12px] text-[#6b6b6b]">
                        {f.email} · Signed {fmtDateTime(f.signed_at)}{f.is_minor ? ' · Minor' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ConsentPill ok={f.consent_treatment} label="TX" />
                    <ConsentPill ok={f.consent_hipaa} label="HIPAA" />
                    <ConsentPill ok={f.consent_financial} label="FIN" />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 pt-2 bg-[#faf6f0]/40 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
                    <div>
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">DATE OF BIRTH</p>
                      <p className="text-[#1c1e20]">{fmtDate(f.date_of_birth)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">PHONE</p>
                      <a href={`tel:${f.phone}`} className="text-[#1c1e20] hover:text-[#c9a961] inline-flex items-center gap-1.5">
                        <Phone size={12} /> {f.phone}
                      </a>
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">EMAIL</p>
                      <a href={`mailto:${f.email}`} className="text-[#1c1e20] hover:text-[#c9a961] inline-flex items-center gap-1.5">
                        <Mail size={12} /> {f.email}
                      </a>
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">ADDRESS</p>
                      <p className="text-[#1c1e20]">{address || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">EMERGENCY CONTACT</p>
                      <p className="text-[#1c1e20]">
                        {f.emergency_contact_name || '—'}{f.emergency_contact_phone ? ` · ${f.emergency_contact_phone}` : ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">GUARDIAN (IF MINOR)</p>
                      <p className="text-[#1c1e20]">
                        {f.is_minor ? `${f.guardian_name || '—'} (${f.guardian_relationship || '—'})` : 'N/A'}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-1">ALLERGIES / CONDITIONS</p>
                      <p className="text-[#1c1e20]">{f.allergies_conditions || '—'}</p>
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between pt-2 border-t border-black/5 mt-2">
                      <div>
                        <p className="text-[10px] tracking-[0.24em] text-[#6b6b6b] font-semibold mb-0.5">SIGNED AS</p>
                        <p
                          className="text-[#1c1e20] italic text-[18px]"
                          style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                          {f.signature}
                        </p>
                        <p className="text-[11px] text-[#6b6b6b]">
                          Media consent: {f.consent_media ? 'YES' : 'NO'} · IP {f.ip_address || 'unknown'}
                        </p>
                      </div>
                      <button
                        onClick={() => onDelete(f.id)}
                        data-testid={`consent-delete-${f.id}`}
                        className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-[12px] font-semibold"
                      >
                        <Trash2 size={13} /> REMOVE
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PatientFormsTab;
